import type {DefaultError} from "@tanstack/react-query";
import type {UrlQueryKey} from "~/query/queryClient.ts";
import {queryClient, useCustomQuery} from "~/query/queryClient.ts";
import {queryHttp} from "~/query/queryHttp.ts";
import type {PageResponse} from "~/services/ApiTypes.ts";
import type {ContractRow, ContractSummaryResponse} from "./contractExplorerTypes.ts";

/** `GET /api/data/{databag_id}/contracts/` */
export const contractsUrl = (bagId: number) => `/data/${bagId}/contracts/`;
/** `GET /api/data/{databag_id}/contracts/summary/` */
export const contractsSummaryUrl = (bagId: number) => `${contractsUrl(bagId)}summary/`;

export type ContractPageParams = {
    bagId: number;
    offset: number;
    /** Rows to fetch (the API caps it at 500). */
    limit: number;
    /** Server-side search over title, supplier, label and bundle id. */
    search?: string;
    lifecycle?: string;
    documentType?: string;
    needsAttention?: boolean;
    /** DRF ordering: `-linked_spend`, `title`, `ends_on` … (see `ORDERING_KEYS`). */
    ordering?: string;
};

function requestParams(p: ContractPageParams): Record<string, string | number> {
    const params: Record<string, string | number> = {offset: p.offset, limit: p.limit};
    if (p.search) params.search = p.search;
    if (p.lifecycle) params.lifecycle = p.lifecycle;
    if (p.documentType) params.document_type = p.documentType;
    if (p.needsAttention) params.needs_attention = 'true';
    if (p.ordering) params.ordering = p.ordering;
    return params;
}

/**
 * One window of the bundle list (DRF LimitOffset envelope), through the
 * query client so windows stay cached and de-duplicated: the grid's own
 * data-source cache is off because its key does not include our params.
 */
export async function fetchContractPage(p: ContractPageParams): Promise<{rows: ContractRow[]; total: number}> {
    const data = await queryClient.fetchQuery<PageResponse<ContractRow>>({
        queryKey: [contractsUrl(p.bagId), {params: requestParams(p)}] satisfies UrlQueryKey,
        staleTime: 30_000,
    });
    return {rows: data.results ?? [], total: data.count ?? 0};
}

/**
 * One document of a bundle, streamed through the API from its copy in the
 * customer's private media bucket (only when the detail says `has_file`).
 */
export async function fetchContractDocumentFile(bagId: number, contractId: number, documentId: string): Promise<Blob> {
    const {data} = await queryHttp.get<Blob>(
        `${contractsUrl(bagId)}${contractId}/documents/${encodeURIComponent(documentId)}/file/`,
        {responseType: 'blob'},
    );
    return data;
}

/**
 * The whole-databag figures: lifecycle and type counts, totals, the spend
 * currency and the latest import. They change only with an import, so this is
 * fetched once per databag.
 */
export function useContractSummaryQuery(bagId: number) {
    return useCustomQuery<ContractSummaryResponse, DefaultError, ContractSummaryResponse, UrlQueryKey>({
        queryKey: [contractsSummaryUrl(bagId)],
        enabled: bagId > 0,
        staleTime: 5 * 60_000,
    });
}
